// @ts-expect-error Could not find a declaration file for module
import BasePatternFactory from 'PatternValidationJS/lib/BasePatternFactory';
// @ts-expect-error Could not find a declaration file for module
import EmailAddressPattern from 'PatternValidationJS/patterns/EmailAddress';
// @ts-expect-error Could not find a declaration file for module
import HexCodePattern from 'PatternValidationJS/patterns/HexCode';
// @ts-expect-error Could not find a declaration file for module
import UrlPattern from 'PatternValidationJS/patterns/Url';
// @ts-expect-error Could not find a declaration file for module
import UrlParts from 'PatternValidationJS/regex/parts/UrlParts';
import ValidationError from 'ContentData/models/ValidationError';
const ANONYMOUS_TYPE_NAME = '<<?>>';
const getTypeName = value => {
  if (value == null) {
    return ANONYMOUS_TYPE_NAME;
  }
  return value.constructor && value.constructor.name || ANONYMOUS_TYPE_NAME;
};
const isRequired = value => {
  if (value == null) {
    if (value === null) {
      return new ValidationError('value is marked as required but is `null`');
    }
    return new ValidationError('value is marked as required but is `undefined`');
  }
  return null;
};
export const createChainableValidator = validator => {
  const checker = (required, value, ...args) => {
    if (value == null) {
      return required ? isRequired(value) : null;
    } else {
      return validator(value, ...args);
    }
  };
  const chainedChecker = checker.bind(null, false);
  chainedChecker.isRequired = checker.bind(null, true);
  return chainedChecker;
};
const createPrimitiveTypeValidator = expectedType => {
  return createChainableValidator(value => {
    const isExpectedType = Object.prototype.toString.call(value) === `[object ${expectedType}]`;
    if (!isExpectedType) {
      return new ValidationError(`expected value to be a '${expectedType}', received '${getTypeName(value)}'`);
    }
    return null;
  });
};
export const isArray = createPrimitiveTypeValidator('Array');
export const isBoolean = createPrimitiveTypeValidator('Boolean');
export const isFunction = createPrimitiveTypeValidator('Function');
export const isNumber = createPrimitiveTypeValidator('Number');
export const isObject = createPrimitiveTypeValidator('Object');
export const isString = createPrimitiveTypeValidator('String');
export const arrayOf = checker => createChainableValidator(value => {
  const arrayError = isArray(value);
  if (arrayError) {
    return arrayError;
  }
  for (let i = 0; i < value.length; i++) {
    const error = checker(value[i]);
    if (error instanceof ValidationError) {
      return error;
    }
  }
  return null;
});
export const instanceOf = expectedType => createChainableValidator(value => {
  if (!(value instanceof expectedType)) {
    const expectedTypeName = expectedType.name || ANONYMOUS_TYPE_NAME;
    const valueTypeName = getTypeName(value);
    return new ValidationError(`value must be instanceof '${expectedTypeName}', received '${valueTypeName}'`);
  }
  return null;
});
export const tupleOfTypes = checkers => createChainableValidator(value => {
  const arrayError = isArray(value);
  if (arrayError instanceof ValidationError) {
    return arrayError;
  }
  if (value.length !== checkers.length) {
    return new ValidationError('value and types should be the same length');
  }
  for (let i = 0; i < value.length; i++) {
    const checker = checkers[i];
    const error = checker(value[i]);
    if (error instanceof ValidationError) {
      return error;
    }
  }
  return null;
});
export const oneOf = expectedValues => createChainableValidator(value => {
  if (!expectedValues.includes(value)) {
    return new ValidationError(`value must be one of '${expectedValues.join(', ')}'`);
  }
  return null;
});
export const oneOfType = expectedTypes => createChainableValidator(value => {
  let error;
  for (let i = 0; i < expectedTypes.length; i++) {
    const checker = expectedTypes[i];
    error = checker(value);
    if (error === null) {
      return null;
    }
  }
  return new ValidationError(`invalid value type, received '${getTypeName(value)}'` + `\nValidationError: ${error}`);
});
const checkShape = (value, shapeTypes, strict = false) => {
  const objectError = isObject(value);
  if (objectError instanceof ValidationError) {
    return objectError;
  }
  const shapeKeys = strict ? Object.assign({}, value, shapeTypes) : shapeTypes;
  for (const key in shapeKeys) {
    if (Object.prototype.hasOwnProperty.call(shapeTypes, key)) {
      const checker = shapeTypes[key];
      const keyValue = value[key];
      let error = checker(keyValue);
      if (strict && error === null) {
        error = isRequired(keyValue);
      }
      if (error instanceof ValidationError) {
        return new ValidationError(`invalid key '${key}': ${error}`);
      }
    } else if (strict) {
      return new ValidationError(`invalid key '${key}', expected '${Object.keys(shapeTypes).join(', ')}'`);
    }
  }
  return null;
};
const createChainableShapeValidator = (strict, shapeTypes) => {
  const chainedChecker = createChainableValidator(value => checkShape(value, shapeTypes, strict));
  chainedChecker.extend = shapeTypesOrMerger => {
    let mergedShapeTypes;
    if (isFunction(shapeTypesOrMerger) === null) {
      mergedShapeTypes = shapeTypesOrMerger(shapeTypes);
    } else if (isObject(shapeTypesOrMerger) === null) {
      mergedShapeTypes = Object.assign({}, shapeTypes, shapeTypesOrMerger);
    } else {
      mergedShapeTypes = Object.assign({}, shapeTypes);
    }
    return createChainableShapeValidator(strict, mergedShapeTypes);
  };
  return chainedChecker;
};
export const shape = createChainableShapeValidator.bind(null, false);
export const strictShape = createChainableShapeValidator.bind(null, true);
const createPatternValidator = (pattern, typeName) => createChainableValidator(value => {
  if (!pattern.test(value)) {
    return new ValidationError(`invalid ${typeName} value, received '${value}'`);
  }
  return null;
});

// Exact copy of PatternValidationJS email address regex, but with support for query params
// https://git.hubteam.com/HubSpot/PatternValidation/blob/master/PatternValidationJS/static-1.437/js/regex/EmailAddressRegex.js
const EMAIL_ADDRESS_REGEX_WITH_QUERY_PARAMS = new RegExp(`^(?:.+)@${UrlParts.domain}${UrlParts.search}?$`);
export const EmailAddressWithQueryParamsPattern = BasePatternFactory({
  name: 'Email address with query params',
  validator: input => EMAIL_ADDRESS_REGEX_WITH_QUERY_PARAMS.test(input)
});
export const isEmailAddressWithOptionalQueryParams = createPatternValidator(EmailAddressWithQueryParamsPattern, 'Email address with query params');
export const isEmailAddress = createPatternValidator(EmailAddressPattern, 'email address');
export const isHexCode = createPatternValidator(HexCodePattern, 'hex code');
export const isUrl = createPatternValidator(UrlPattern, 'URL');
const ANCHOR_REGEX = new RegExp(/^#\S/);
const TEL_REGEX = new RegExp(/^tel:\S/);
const RELATIVE_URL_REGEX = new RegExp(/^\/\S/);
const WHITESPACE_REGEX = new RegExp(/\s/);
// Exact copy of UrlRegexFactory from PatternValidationJS, but with required protocol
// https://git.hubteam.com/HubSpot/PatternValidation/blob/master/PatternValidationJS/static-1.437/js/regex/factories/UrlRegexFactory.js
const URL_REGEX_WITH_REQUIRED_PROTOCOL_REGEX = new RegExp(`^${UrlParts.protocol}${UrlParts.domain}(?:${UrlParts.path}(?:${UrlParts.file})?)?${UrlParts.search}?$`, 'i');
export const ExternalUrlPattern = BasePatternFactory({
  name: 'External URL',
  validator: input => {
    return ANCHOR_REGEX.test(input) || TEL_REGEX.test(input) || RELATIVE_URL_REGEX.test(input) || URL_REGEX_WITH_REQUIRED_PROTOCOL_REGEX.test(input);
  }
});
export const isValidExternalUrl = createPatternValidator(ExternalUrlPattern, 'External URL');
export const isRegExp = createChainableValidator(value => {
  try {
    // eslint-disable-next-line no-new
    new RegExp(value);
    return null;
  } catch (e) {
    return new ValidationError(`invalid regex value, received '${value}'\n${e}`);
  }
});
export const isCssString = oneOf(['%', 'ch', 'em', 'ex', 'in', 'lh', 'pc', 'pt', 'px', 'Q', 'rem', 'vh', 'vmax', 'vmin', 'vw']);
export const validateWhiteSpace = input => {
  //https://git.hubteam.com/HubSpot/themes-and-modules/issues/849
  //white space makes validation take longer so return immediately when there is white space
  return !WHITESPACE_REGEX.test(input);
};